/**
 * <sunday-delivery>: pincode in, delivery date out.
 *
 * The date comes from ShipZip, the same service the live site and checkout use, so the
 * promise here matches the one at checkout. If ShipZip doesn't answer in time, a simple
 * fallback by pincode prefix is shown and marked as an estimate.
 * A custom size adds one day (it's cut to order).
 *
 * data-mode="zones" (info and city pages): no product, so no ShipZip call; the answer is the
 * store-facts days for the pincode's zone: "By Sat 24 Oct · Free delivery" (today + the zone's upper bound),
 * the range under it in the page's own words ("{min} to {max} days"). The words are the widget's parameters.
 * One element for every pincode field on the site; the pincode is remembered across pages.
 * A page can open with ?pincode=560001: the field is filled and the check runs (16 Oct).
 * The answer (v93): "Arrives today." / "Arrives tomorrow, Wed 14 Oct." / "Arrives Sat 17 Oct – Mon 19 Oct.",
 * then, where cash or card is taken at the door, "Pay cash or card at the door." Free delivery is said in the ticks.
 * No jump on return visits: the last answer is kept (state "pinout": pincode, product, day, html) and the
 * widget's snippet puts it back before first paint when the pincode, product and day still match; the fresh
 * answer then replaces it, nearly always with the same lines.
 */
import * as state from '@sunday/state';
import { money, fill } from '@sunday/util';

/* ShipZip answers in English ('26 Sep'); these are only for reading its text, never shown. */
const SHIPZIP_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TIMEOUT_MS = 6000;

/* 15 Oct (AR, iPhone): the pincode field takes focus only from the person's own tap or key. Safari can focus it on
   its own (restoring a page, AutoFill), which opened the keypad on every load; any such focus is let go at once. */
let lastTouch = -1e9;
for (const ev of ['pointerdown', 'touchstart', 'mousedown', 'keydown']) addEventListener(ev, () => (lastTouch = performance.now()), { capture: true, passive: true });
const notByHand = () => performance.now() - lastTouch > 1000;
const letGo = () => {
  const a = document.activeElement;
  if (a && a.matches && a.matches('sunday-delivery input') && notByHand()) a.blur();
};
addEventListener('pageshow', letGo);
addEventListener('load', letGo);

/* Dates are shown in the page's language by the browser ('Sat 26 Sep', 'शनि, 26 सित॰'), so no day or month names live in code. */
const lang = (document.documentElement.lang || 'en').toLowerCase();
const EN = lang.startsWith('en');
const dateFmt = new Intl.DateTimeFormat(EN ? 'en-US' : `${lang}-IN`, { weekday: 'short', day: 'numeric', month: 'short' });
/** [weekday, rest] so a narrow box can drop the weekday; outside English the browser's own order, unsplit. */
export const dayParts = (d) => {
  if (!EN) return ['', dateFmt.format(d)];
  const p = Object.fromEntries(dateFmt.formatToParts(d).map((x) => [x.type, x.value]));
  return [p.weekday, `${p.day} ${p.month}`]; // Indian English order: Sat 26 Sep
};
export const fmt = (d) => dayParts(d).filter(Boolean).join(' ');
export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
/** Today as y-m-d in the visitor's own calendar (the same key the snippet's inline script makes). */
const dayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
/** 0 for today, 1 for tomorrow, else the number of calendar days from today. */
export const daysFromToday = (d) => {
  const a = new Date();
  a.setHours(0, 0, 0, 0);
  const b = new Date(d);
  b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
};

/** '26 Sep' → a Date this year (or next January, if the month has already passed). */
function parseDay(s) {
  const m = /(\d{1,2})\s+([A-Z][a-z]{2})/.exec(s || '');
  if (!m) return null;
  const now = new Date();
  const d = new Date(now.getFullYear(), SHIPZIP_MONTHS.indexOf(m[2]), Number(m[1]));
  if (d < addDays(now, -30)) d.setFullYear(d.getFullYear() + 1);
  return d;
}

class SundayDelivery extends HTMLElement {
  connectedCallback() {
    this.cfg = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.zonesOnly = this.dataset.mode === 'zones';
    this.custom = !!window.sundayBuyLast?.custom;
    this.result = null;
    this.input = this.querySelector('input');
    this.out = this.querySelector('[data-out]');
    this.input.addEventListener('focus', () => notByHand() && this.input.blur());
    requestAnimationFrame(letGo);
    this.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.check(this.input.value);
    });
    this.input.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      if (this.input.value.length === 6) this.check(this.input.value);
    });
    document.addEventListener('sunday:buy', (e) => {
      const was = this.custom;
      this.custom = !!e.detail?.custom;
      if (this.result && was !== this.custom) this.show(this.result);
    });
    // a link can carry the pincode (?pincode=560001): it is filled and checked at once; else the remembered one
    let fromLink = '';
    try { fromLink = new URLSearchParams(location.search).get('pincode') || ''; } catch (e) { fromLink = ''; }
    const remembered = /^[1-9]\d{5}$/.test(fromLink) ? fromLink : state.get('pincode');
    if (remembered) {
      this.input.value = remembered;
      this.check(remembered, true);
    }
  }

  async check(pin, quiet = false) {
    const seq = (this.seq = (this.seq || 0) + 1); // only the latest check may answer
    if (!/^[1-9]\d{5}$/.test(pin)) {
      if (!quiet) {
        this.say(`<p class="sd-msg">${this.cfg.text.invalid}</p>`);
        this.#tell(null);
      }
      return;
    }
    state.set('pincode', pin);
    this.pin = pin;
    if (this.zonesOnly) {
      const z = this.#zone(pin);
      const t = this.cfg.text;
      const range = `<p class="sd-msg sd-range">${fill(t.found, { min: z.min, max: z.max })}</p>`;
      if (t.by) {
        const by = fill(t.by, { date: fmt(addDays(new Date(), Number(z.max) || 0)) });
        this.say(`<p class="sd-when sd-by"><b class="sunday-num">${by}${t.freeLine ? ` · ${t.freeLine}` : ''}</b></p>${range}`);
      } else {
        this.say(`<p class="sd-when">${fill(t.found, { min: z.min, max: z.max })}</p>`);
      }
      return;
    }
    // a remembered answer is already showing (put back before paint): keep it until the fresh one comes
    if (!(quiet && this.out.dataset.pre)) this.say(`<p class="sd-msg">${this.cfg.text.checking}</p>`);
    let res = null;
    try {
      res = await this.#fromShipZip(pin);
    } catch (e) {
      res = null;
    }
    if (seq !== this.seq) return;
    this.result = res || this.#fallback(pin);
    this.show(this.result);
  }

  async #fromShipZip(pin) {
    const c = this.cfg;
    const body = new URLSearchParams();
    body.set('rate[destination][postal_code]', pin);
    body.set('rate[destination][province]', '');
    body.set('rate[destination][country]', '');
    body.set('rate[items][0][quantity]', '1');
    body.set('rate[items][0][grams]', String(c.grams || 0));
    body.set('rate[items][0][product_id]', String(c.productId));
    body.set('rate[items][0][price]', String(c.price || 0));
    body.set('rate[items][0][vendor]', c.vendor || '');
    body.set('shop', c.shop);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    const r = await fetch(c.endpoint, { method: 'POST', body, signal: ctrl.signal, headers: { Accept: 'application/json' } });
    clearTimeout(timer);
    if (!r.ok) return null;
    const data = await r.json();
    // ShipZip returns every service for the pincode: free ones, and sometimes a paid faster one
    // (e.g. Bengaluru: free in 2–3 days, or same/next day for ₹500). Lead with the free date;
    // mention the paid one only if it's faster.
    const options = [];
    for (const rate of data?.rates || []) {
      const desc = String(rate.description || '');
      const days = desc.match(/\b\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g);
      if (!days || !days.length) continue;
      const from = parseDay(days[0]);
      const to = parseDay(days[1] || days[0]);
      if (!from) continue;
      options.push({ from, to, price: Number(rate.total_price) || 0, name: String(rate.service_name || rate.name || '').trim() });
    }
    if (!options.length) return null;
    const byDate = (a, b) => a.from - b.from;
    const free = options.filter((o) => o.price === 0).sort(byDate);
    const paid = options.filter((o) => o.price > 0).sort(byDate);
    const main = free[0] || paid[0];
    const faster = free.length && paid.length && paid[0].from < free[0].from ? paid[0] : null;
    return {
      from: main.from,
      to: main.to,
      charge: main.price,
      faster,
      cod: options.some((o) => /\bCOD\b/i.test(o.name)) || this.#local(pin),
      estimate: false,
    };
  }

  #zone(pin) {
    return (this.cfg.zones || []).find((z) => z.prefix && pin.startsWith(z.prefix)) || this.cfg.zoneDefault;
  }

  #fallback(pin) {
    const z = this.#zone(pin);
    const today = new Date();
    return { from: addDays(today, z.min), to: addDays(today, z.max), charge: 0, faster: null, cod: this.#local(pin), estimate: true };
  }

  /* The store's own city (the first zone, store facts → local_pin_start): cash or card at the door. */
  #local(pin) {
    const z = (this.cfg.zones || [])[0];
    return !!(z && z.prefix && pin.startsWith(z.prefix));
  }

  show(r) {
    const t = this.cfg.text;
    const extra = this.custom ? 1 : 0;
    const span = (from, to) => {
      const a = addDays(from, extra);
      const b = addDays(to, extra);
      return +a === +b ? fmt(a) : `${fmt(a)} – ${fmt(b)}`;
    };
    const a = addDays(r.from, extra);
    const one = +addDays(r.to, extra) === +a;
    const n = daysFromToday(a);
    let when = span(r.from, r.to);
    if (one && n === 0) when = t.today;
    else if (one && n === 1) when = fill(t.tomorrow, { date: fmt(a) });
    const label = r.estimate ? t.estimate : t.arrives;
    const lines = [`<p class="sd-when"><b class="sunday-num">${label} ${when}.</b></p>`];
    if (r.charge > 0) lines.push(`<p class="sd-msg">${t.charge.replace('[amount]', money(r.charge))}</p>`);
    if (r.faster) lines.push(`<p class="sd-msg sd-sooner">${t.sooner.replace('[dates]', span(r.faster.from, r.faster.to)).replace('[amount]', money(r.faster.price))}</p>`);
    if (r.cod) lines.push(`<p class="sd-msg">${t.cod}</p>`);
    if (this.custom) lines.push(`<p class="sd-msg">${t.customDay}</p>`);
    this.say(lines.join(''));
    delete this.out.dataset.pre;
    state.set('pinout', { pin: this.pin, pid: String(this.cfg.productId || ''), day: dayKey(), custom: this.custom, html: lines.join('') });
    const last = addDays(r.to, extra);
    this.#tell({ today: one && n === 0, parts: dayParts(last) });
  }

  /* The date for anything else in the buy box that shows it (the store tiles): sunday:eta, null when there is none. */
  #tell(detail) {
    this.eta = detail;
    this.dispatchEvent(new CustomEvent('sunday:eta', { bubbles: true, detail }));
  }

  say(html) {
    this.out.innerHTML = html;
  }
}

if (!customElements.get('sunday-delivery')) customElements.define('sunday-delivery', SundayDelivery);
